import { useCallback, useEffect, useState } from "react";
import { Camera, Check, Mic, Pencil, Search, Tags, Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest, convertFileToDataUrl, formatDateTime } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { hapticSuccess, takePhoto, isNative } from "@/lib/native-bridge";

const initialForm = { description: "", event_id: "", title: "" };

export const MemoryVaultPage = ({ token, user }) => {
  const [events, setEvents] = useState([]);
  const [memories, setMemories] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [imageFile, setImageFile] = useState(null);
  const [voiceFile, setVoiceFile] = useState(null);
  const [voiceDataUrl, setVoiceDataUrl] = useState(null);
  const [nativePhotoUrl, setNativePhotoUrl] = useState(null);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [editingMemory, setEditingMemory] = useState(null);
  const [deletingMemoryId, setDeletingMemoryId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetagging, setIsRetagging] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [eventsPayload, memoriesPayload] = await Promise.all([
        apiRequest("/events", { token }),
        apiRequest("/memories", { token }),
      ]);
      setEvents(eventsPayload || []);
      setMemories(memoriesPayload || []);
      setForm((current) => ({ ...current, event_id: current.event_id || eventsPayload?.[0]?.id || "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load memory vault.");
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateMemory = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const [imageDataUrl, voiceFileDataUrl] = await Promise.all([
        convertFileToDataUrl(imageFile),
        convertFileToDataUrl(voiceFile),
      ]);
      const finalImage = nativePhotoUrl || imageDataUrl;
      const finalVoice = voiceDataUrl || voiceFileDataUrl;
      const payload = await apiRequest("/memories", {
        method: "POST",
        data: {
          ...form,
          image_data_url: finalImage || undefined,
          voice_note_data_url: finalVoice || undefined,
        },
        token,
      });
      setMemories((current) => [payload, ...current]);
      setForm({ ...initialForm, event_id: form.event_id || events[0]?.id || "" });
      setImageFile(null);
      setVoiceFile(null);
      setVoiceDataUrl(null);
      setNativePhotoUrl(null);
      await hapticSuccess();
      toast.success("Memory saved with tags.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save this memory.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComment = async (memoryId) => {
    try {
      const payload = await apiRequest(`/memories/${memoryId}/comments`, {
        method: "POST",
        data: { text: commentDrafts[memoryId] },
        token,
      });
      setMemories((current) => current.map((memory) => (memory.id === payload.id ? payload : memory)));
      setCommentDrafts((current) => ({ ...current, [memoryId]: "" }));
      toast.success("Comment added.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to add your comment.");
    }
  };

  const handleSaveEditMemory = async () => {
    if (!editingMemory) return;
    try {
      const payload = await apiRequest(`/memories/${editingMemory.id}`, {
        method: "PUT",
        token,
        data: { title: editingMemory.title, description: editingMemory.description },
      });
      setMemories((current) => current.map((m) => (m.id === payload.id ? { ...m, ...payload } : m)));
      setEditingMemory(null);
      toast.success("Memory updated.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update memory.");
    }
  };

  const handleDeleteMemory = async (memoryId) => {
    try {
      await apiRequest(`/memories/${memoryId}`, { method: "DELETE", token });
      setMemories((current) => current.filter((m) => m.id !== memoryId));
      setDeletingMemoryId("");
      toast.success("Memory deleted.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete memory.");
    }
  };

  const handleBatchRetag = async () => {
    setIsRetagging(true);
    try {
      const result = await apiRequest("/memories/batch-retag", { method: "POST", token });
      toast.success(`Re-tagged ${result.updated} memories with improved AI analysis.`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to re-tag memories.");
    } finally {
      setIsRetagging(false);
    }
  };

  const runSearch = async (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    if (!q) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const res = await apiRequest(`/memory/search?q=${encodeURIComponent(q)}`, { token });
      setSearchResults(res);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="archival-card">
        <p className="eyebrow-text">Memory vault</p>
        <h2 className="mt-3 font-display text-3xl text-foreground sm:text-4xl" data-testid="memories-page-title">
          Turn every gathering into a living archive.
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-7 text-muted-foreground sm:text-base" data-testid="memories-page-copy">
          Upload event-linked photos, add voice-note memories, and let AI generate helpful tags for future discovery.
        </p>
      </section>

      <section className="archival-card" data-testid="memory-search">
        <form className="flex flex-col gap-2 sm:flex-row" onSubmit={runSearch}>
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="field-input w-full pl-9"
              data-testid="memory-search-input"
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search every memory and story…"
              value={searchQ}
            />
          </div>
          <Button className="rounded-full" data-testid="memory-search-submit" disabled={searching} size="sm" type="submit">
            {searching ? "Searching…" : "Search the archive"}
          </Button>
        </form>
        {searchResults && (
          <div className="mt-4 space-y-2" data-testid="memory-search-results">
            <p className="text-xs text-muted-foreground">{searchResults.total} result{searchResults.total === 1 ? "" : "s"} for “{searchResults.query}”</p>
            {searchResults.memories?.map((m) => (
              <div className="soft-panel" data-testid={`memory-search-memory-${m.id}`} key={m.id}>
                <p className="text-sm font-semibold text-foreground">{m.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">Memory · {m.ai_summary || m.description}</p>
              </div>
            ))}
            {searchResults.threads?.map((t) => (
              <div className="soft-panel" data-testid={`memory-search-thread-${t.id}`} key={t.id}>
                <p className="text-sm font-semibold text-foreground">{t.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">Story · {t.category}</p>
              </div>
            ))}
            {searchResults.total === 0 && <p className="text-sm text-muted-foreground">Nothing matched yet — try another word.</p>}
          </div>
        )}
      </section>

      <section className="archival-card" data-testid="memories-create-section">
        <div className="flex items-center gap-3">
          <Camera className="h-5 w-5 text-primary" />
          <h3 className="font-display text-3xl text-foreground">Add a memory</h3>
        </div>
        <form className="mt-6 grid gap-4 xl:grid-cols-2" onSubmit={handleCreateMemory}>
          <label>
            <span className="field-label">Event</span>
            <select className="field-input w-full" data-testid="memories-event-select" onChange={(e) => setForm((current) => ({ ...current, event_id: e.target.value }))} required value={form.event_id}>
              <option value="">Select an event</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>{event.title}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="field-label">Title</span>
            <Input className="field-input" data-testid="memories-title-input" onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))} required value={form.title} />
          </label>
          <label className="xl:col-span-2">
            <span className="field-label">Context or story</span>
            <Textarea className="field-textarea" data-testid="memories-description-input" onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))} required value={form.description} />
          </label>
          <label>
            <span className="field-label">Photo upload</span>
            {isNative() && (
              <Button
                className="rounded-full mb-2 w-full"
                data-testid="memories-native-camera"
                onClick={async () => {
                  const url = await takePhoto();
                  if (url) setNativePhotoUrl(url);
                }}
                size="sm"
                type="button"
                variant="outline"
              >
                <Camera className="mr-1 h-3.5 w-3.5" /> Take Photo
              </Button>
            )}
            {nativePhotoUrl && <img alt="Preview" className="mb-2 rounded-xl max-h-32 object-cover" src={nativePhotoUrl} />}
            <Input className="field-input pt-3" data-testid="memories-image-input" onChange={(e) => setImageFile(e.target.files?.[0] || null)} type="file" accept="image/*" />
          </label>
          <div className="space-y-2">
            <span className="field-label">Voice note</span>
            <VoiceRecorder disabled={isSubmitting} onRecordingComplete={setVoiceDataUrl} />
            <p className="text-xs text-muted-foreground">Or upload a file:</p>
            <Input className="field-input pt-3" data-testid="memories-audio-input" onChange={(e) => setVoiceFile(e.target.files?.[0] || null)} type="file" accept="audio/*" />
          </div>
          <div className="xl:col-span-2">
            <Button className="rounded-full py-6 text-base" data-testid="memories-submit-button" disabled={isSubmitting || !events.length} type="submit">
              {isSubmitting ? "Saving memory..." : "Save memory with AI tags"}
            </Button>
          </div>
        </form>
      </section>

      <section className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
        {memories.length > 0 && (
          <div className="lg:col-span-2 xl:col-span-3 flex justify-end">
            <Button
              className="rounded-full"
              data-testid="memories-batch-retag"
              disabled={isRetagging}
              onClick={handleBatchRetag}
              size="sm"
              variant="outline"
            >
              <Tags className="mr-1 h-3.5 w-3.5" /> {isRetagging ? "Re-tagging..." : "Re-tag all with AI"}
            </Button>
          </div>
        )}
        {memories.length ? (
          memories.map((memory) => (
            <article className="archival-card overflow-hidden" data-testid={`memory-card-${memory.id}`} key={memory.id}>
              {memory.image_data_url ? (
                <div className="overflow-hidden rounded-[24px] bg-muted">
                  <img alt={memory.title} className="aspect-[4/3] w-full object-cover object-center" data-testid={`memory-image-${memory.id}`} src={memory.image_data_url} />
                </div>
              ) : null}
              <div className="mt-5 space-y-4">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    {editingMemory?.id === memory.id ? (
                      <div className="w-full space-y-2">
                        <Input className="field-input" data-testid={`memory-edit-title-${memory.id}`} onChange={(e) => setEditingMemory((c) => ({ ...c, title: e.target.value }))} value={editingMemory.title} />
                        <Textarea className="field-textarea" data-testid={`memory-edit-desc-${memory.id}`} onChange={(e) => setEditingMemory((c) => ({ ...c, description: e.target.value }))} rows={3} value={editingMemory.description} />
                        <div className="flex gap-2">
                          <Button className="rounded-full h-8" data-testid={`memory-edit-save-${memory.id}`} onClick={handleSaveEditMemory} size="sm"><Check className="mr-1 h-3 w-3" /> Save</Button>
                          <Button className="rounded-full h-8" data-testid={`memory-edit-cancel-${memory.id}`} onClick={() => setEditingMemory(null)} size="sm" variant="outline"><X className="mr-1 h-3 w-3" /> Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1">
                          <p className="text-lg font-semibold text-foreground">{memory.title}</p>
                          <p className="mt-2 text-sm text-muted-foreground">{memory.event_title} · {formatDateTime(memory.created_at)}</p>
                          <p className="mt-3 text-sm leading-7 text-muted-foreground">{memory.description}</p>
                        </div>
                        {(memory.created_by || memory.created_by_id) === user?.id ? (
                        <div className="flex gap-1 shrink-0">
                          <button className="rounded-full p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" data-testid={`memory-edit-btn-${memory.id}`} onClick={() => setEditingMemory({ id: memory.id, title: memory.title, description: memory.description || "" })}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button className="rounded-full p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" data-testid={`memory-delete-btn-${memory.id}`} onClick={() => setDeletingMemoryId(memory.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        ) : null}
                      </>
                    )}
                  </div>
                  {deletingMemoryId === memory.id && (
                    <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/5 p-3">
                      <p className="text-sm font-semibold text-destructive">Delete this memory?</p>
                      <div className="mt-2 flex gap-2">
                        <Button className="rounded-full" data-testid={`memory-delete-confirm-${memory.id}`} onClick={() => handleDeleteMemory(memory.id)} size="sm" variant="destructive">Confirm</Button>
                        <Button className="rounded-full" data-testid={`memory-delete-cancel-${memory.id}`} onClick={() => setDeletingMemoryId("")} size="sm" variant="outline">Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="soft-panel" data-testid={`memory-tags-panel-${memory.id}`}>
                  <div className="flex items-center gap-2 text-primary">
                    <Tags className="h-4 w-4" />
                    <p className="text-sm font-semibold">AI tags</p>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {memory.tags.map((tag) => (
                      <span className="rounded-full border border-border bg-background/80 px-3 py-1 text-xs font-semibold text-foreground" data-testid={`memory-tag-${memory.id}-${tag.replace(/\s+/g, "-")}`} key={tag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="mt-3 text-sm leading-7 text-muted-foreground" data-testid={`memory-summary-${memory.id}`}>{memory.ai_summary}</p>
                  {(memory.sentiment || memory.mood) && (
                    <div className="mt-2 flex gap-2" data-testid={`memory-ai-meta-${memory.id}`}>
                      {memory.sentiment && memory.sentiment !== "neutral" && (
                        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">{memory.sentiment}</span>
                      )}
                      {memory.mood && (
                        <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-medium text-accent-foreground">{memory.mood}</span>
                      )}
                    </div>
                  )}
                </div>
                {memory.voice_note_data_url ? (
                  <div className="soft-panel" data-testid={`memory-audio-${memory.id}`}>
                    <div className="mb-3 flex items-center gap-2 text-primary">
                      <Mic className="h-4 w-4" />
                      <p className="text-sm font-semibold">Voice note</p>
                    </div>
                    <audio className="w-full" controls src={memory.voice_note_data_url} />
                  </div>
                ) : null}
                <div className="soft-panel" data-testid={`memory-comments-panel-${memory.id}`}>
                  <p className="text-sm font-semibold text-foreground">Comments</p>
                  <div className="mt-3 space-y-3">
                    {memory.comments.map((comment) => (
                      <div className="rounded-2xl border border-border/70 bg-background/70 px-4 py-3" data-testid={`memory-comment-${comment.id}`} key={comment.id}>
                        <p className="text-sm font-semibold text-foreground">{comment.author_name}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{comment.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-3">
                    <Textarea className="field-textarea" data-testid={`memory-comment-input-${memory.id}`} onChange={(e) => setCommentDrafts((current) => ({ ...current, [memory.id]: e.target.value }))} placeholder="Add a memory note or contextual detail" value={commentDrafts[memory.id] || ""} />
                    <Button className="w-full rounded-full" data-testid={`memory-comment-submit-${memory.id}`} onClick={() => handleComment(memory.id)} type="button" variant="secondary">
                      Post comment
                    </Button>
                  </div>
                </div>
              </div>
            </article>
          ))
        ) : (
          <div className="archival-card lg:col-span-2 xl:col-span-3" data-testid="memories-empty-state">
            <p className="text-sm text-muted-foreground">No memories yet. Upload the first photo or voice note from your community archive.</p>
          </div>
        )}
      </section>
    </div>
  );
};
